import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import type { DialogActionsProps, ModalProps } from '@mui/material';
import { Button, DialogActions, useMediaQuery, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';

import type { BulkEditDialogFormValues } from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';
import {
  BulkEditContext,
  BulkEditStep,
} from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';

import { styles } from './index.styles';

export interface BulkEditDialogActionsProps extends DialogActionsProps {
  onClose: ModalProps['onClose'];
}

/**
 * Bulk editing dialogs action buttons.
 *
 * Note: Implemented as component to access formik context.
 */
const BulkEditDialogActions: FC<BulkEditDialogActionsProps> = ({ onClose }) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const { bulkEditingStep, setBulkEditingStep } = useContext(BulkEditContext);

  const { isValid, handleSubmit } =
    useFormikContext<BulkEditDialogFormValues>();

  /**
   * Advance step.
   */
  const advanceStep = (amount = 1) => {
    setBulkEditingStep((previousStep) => previousStep + amount);
  };

  return (
    <DialogActions className="BulkEditDialogActions-root">
      {bulkEditingStep !== BulkEditStep.SelectType && (
        <Button
          data-test-id="user-admin-bulk-edit-dialog-back-button"
          onClick={() => advanceStep(-1)}
          startIcon={<ArrowBackIcon />}
          sx={[styles.actionButton, { mr: 'auto' }]}
          variant="text"
        >
          {upSm && t('back')}
        </Button>
      )}

      <Button
        variant="cta"
        color="secondary"
        data-test-id="user-admin-bulk-edit-dialog-cancel-button"
        onClick={(event) => !!onClose && onClose(event, 'backdropClick')}
      >
        {upSm && t('cancel')}
      </Button>

      {bulkEditingStep !== BulkEditStep.ConfirmChanges && (
        <Button
          variant="cta"
          data-test-id="user-admin-bulk-edit-dialog-next-button"
          disabled={!isValid}
          endIcon={<ArrowForwardIcon />}
          onClick={() => advanceStep()}
          sx={styles.actionButton}
        >
          {upSm && t('next')}
        </Button>
      )}

      {bulkEditingStep === BulkEditStep.ConfirmChanges && (
        <Button
          variant="cta"
          data-test-id="user-admin-bulk-edit-dialog-confirm-button"
          disabled={!isValid}
          endIcon={<CheckIcon />}
          onClick={() => handleSubmit()}
          sx={styles.actionButton}
        >
          {upSm && t('common:confirm')}
        </Button>
      )}
    </DialogActions>
  );
};

export default BulkEditDialogActions;
